import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase, authService, profileService } from '../lib/supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  // Flag para fluxo de recuperação de senha — impede redirect pelo RotaPublica
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)

  useEffect(() => {
    // Carrega sessão inicial
    authService.getSession().then(session => {
      setUser(session?.user ?? null)
      if (session?.user) carregarPerfil(session.user.id)
      else setLoading(false)
    })

    // Escuta mudanças de auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true)
      }
      if (event === 'USER_UPDATED') {
        // Senha foi atualizada — limpa o modo recovery
        setIsPasswordRecovery(false)
      }
      setUser(session?.user ?? null)
      if (session?.user) carregarPerfil(session.user.id)
      else { setProfile(null); setLoading(false) }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function carregarPerfil(userId) {
    try {
      const p = await profileService.getProfile(userId)
      setProfile(p)
      // Sincroniza plano com Stripe em background (1x por sessão, só se tiver plano pago)
      // Garante que plano_expira_em esteja atualizado mesmo se webhook falhou
      if (p?.plano && p.plano !== 'free' && p.stripe_customer_id) {
        sincronizarPlanoEmBackground()
      }
    } catch (e) {
      console.error('Erro ao carregar perfil:', e)
    } finally {
      setLoading(false)
    }
  }

  // Chave para limitar sync a 1x por sessão (por aba/reload)
  const SYNC_KEY = 'cifrasband_plano_synced_at'

  async function sincronizarPlanoEmBackground() {
    try {
      // Só sincroniza 1x por hora para não sobrecarregar a API
      const ultimoSync = sessionStorage.getItem(SYNC_KEY)
      if (ultimoSync && Date.now() - Number(ultimoSync) < 60 * 60 * 1000) return

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      const res = await fetch('/api/stripe/sync-subscription', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (!res.ok) return

      const data = await res.json()
      sessionStorage.setItem(SYNC_KEY, String(Date.now()))

      // Se o plano mudou (ex: expirou no Stripe), recarrega o perfil
      if (data.synced) {
        const p = await profileService.getProfile(session.user.id)
        setProfile(p)
      }
    } catch (e) {
      // Silencioso — não bloqueia a UI se falhar
    }
  }

  async function signUp(email, password, nome, instrumento) {
    await authService.signUp({ email, password, nome, instrumento })
  }

  async function signIn(email, password) {
    await authService.signIn({ email, password })
  }

  async function signOut() {
    await authService.signOut()
    setUser(null)
    setProfile(null)
  }

  async function atualizarPerfil(dados) {
    if (!user) return
    const atualizado = await profileService.updateProfile(user.id, dados)
    setProfile(atualizado)
    return atualizado
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, isPasswordRecovery, signUp, signIn, signOut, atualizarPerfil, carregarPerfil }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve estar dentro de AuthProvider')
  return ctx
}
